/**
 * @file constants/solutionData.js
 * @description SolutionPage 및 HomePage에서 사용하는 RWS·MES·WMS 솔루션 정적 데이터입니다.
 * CMMS·FEMS·withEAI는 각자의 Overview 컴포넌트(WithCMMSOverview, WithFEMSOverview, WithEAIOverview)에서
 * 자체적으로 데이터를 관리하므로 이 파일에 포함하지 않습니다.
 */

export const solutionItems = [
  {
    id: 'rws',
    href: '/solution/rws.html',
    navLabel: 'withRWS',
    badge: 'Raw Material Weighing System',
    title: 'withRWS 칭량관리 시스템',
    summary: '제약·화장품 제조 현장의 원료 준비, 칭량, 검증, 기록을 표준 작업 흐름으로 관리하는 원료 칭량 특화 시스템입니다. 작업 지시를 기준으로 원료와 Lot를 확인하고, 바코드·저울·전자기록을 연계해 반복적인 칭량 오류와 기록 누락을 줄입니다.',
    portfolioTitle: ['원료 준비부터 기록까지', '표준 칭량 흐름을 관리합니다'],
    highlights: [
      {
        label: '핵심 포인트',
        text: '원료, Lot, 칭량값을 작업 지시와 연결해 칭량 오류를 줄입니다.',
      },
      {
        label: '운영 방식',
        text: '바코드와 저울 데이터를 연계해 단계별로 검증하며 칭량합니다.',
      },
      {
        label: '데이터 관리',
        text: '칭량 결과, 작업자, 시간, Lot 정보를 전자기록으로 저장합니다.',
      },
    ],
    portfolioDesc: '생산 지시 또는 배합 기준에 따라 필요한 원료와 칭량량을 안내하고, 스캔과 저울 데이터를 통해 원료·Lot·중량 정보를 단계별로 검증합니다.',
    keyModules: [
      {
        label: '칭량 작업 지시 관리',
        icon: 'list',
        desc: '생산 지시와 배합 기준을 기준으로 원료별 목표 중량, 작업 순서, 진행 상태를 표준화해 관리합니다.',
      },
      {
        label: 'Lot · 사용기한 추적',
        icon: 'record',
        desc: '원료 Lot와 사용기한을 작업 지시와 연결해 잘못된 원료 투입과 추적 누락을 방지합니다.',
      },
      {
        label: '바코드 · 저울 검증',
        icon: 'scan',
        desc: '바코드 스캔과 저울 데이터를 연계해 원료, Lot, 실측 중량을 단계별로 검증합니다.',
      },
      {
        label: '전자서명 · 감사 로그',
        icon: 'sign',
        desc: '작업자 확인, 이중 검토, 재칭량 사유, 변경 이력을 전자기록으로 남겨 감사 대응력을 높입니다.',
      },
    ],
    features: [
      '칭량 작업 지시 및 진행 관리',
      '원료 바코드 스캔 검증',
      'Lot, 사용기한 확인',
      '저울 인터페이스 연동',
      '허용 오차 기준 관리',
      '전자서명 및 이중 확인',
      '칭량 이력 조회 및 리포트',
      '감사 추적 로그 관리',
    ],
    effects: [
      '칭량 오류 및 재작업 감소',
      '원료 사용 정확도 향상',
      'Lot 추적성 강화',
      '감사 대응 시간 단축',
      '표준 작업 절차 정착',
    ],
  },
  {
    id: 'mes',
    href: '/solution/mes.html',
    navLabel: 'withMES',
    badge: 'Manufacturing Execution System',
    title: 'withMES 제조실행 시스템',
    summary: '생산 계획과 현장 실행 사이의 간극을 줄이고, 작업 지시부터 공정 진행, 설비 상태, 품질 검사, 생산 실적까지 하나의 흐름으로 관리하는 제조 실행 플랫폼입니다. 현장의 진행 상황과 이상 징후를 빠르게 파악해 제조 운영의 통제력을 높입니다.',
    portfolioTitle: ['생산 계획과 현장 실행을', '하나의 흐름으로 연결합니다'],
    highlights: [
      {
        label: '핵심 포인트',
        text: '생산 지시부터 실적, 품질, 설비 상태까지 제조 실행 데이터를 연결합니다.',
      },
      {
        label: '운영 방식',
        text: '작업 오더 기준으로 공정을 진행하고 실적과 이상 정보를 수집합니다.',
      },
      {
        label: '데이터 관리',
        text: '생산·품질·설비 데이터를 대시보드와 리포트로 제공합니다.',
      },
    ],
    portfolioDesc: '생산 오더를 기준으로 공정별 작업 지시를 생성하고, 시작·종료, 투입 자재, 설비 상태, 검사 결과, 생산 수량을 관리자 화면과 리포트로 연결합니다.',
    keyModules: [
      {
        label: '생산 오더 관리',
        icon: 'list',
        desc: '생산 계획을 작업 오더로 전환하고 공정별 지시, 담당, 일정, 진행 상태를 관리합니다.',
      },
      {
        label: '공정 진행 모니터링',
        icon: 'chart',
        desc: '공정별 지시·진행·완료 현황을 대시보드로 확인해 생산 병목과 지연 요인을 빠르게 파악합니다.',
      },
      {
        label: '실적 · 설비 · 품질 수집',
        icon: 'collect',
        desc: '현장의 생산 실적, 설비 상태, 품질 검사 결과를 작업 오더 기준으로 수집하고 연결합니다.',
      },
      {
        label: '전자 배치기록 관리',
        icon: 'record',
        desc: '공정 작업 이력과 검토 정보를 전자 배치기록으로 관리해 제조 기록의 추적성을 높입니다.',
      },
    ],
    features: [
      '생산 지시 및 작업 오더 관리',
      '공정 진행 상태 모니터링',
      '작업 실적 수집',
      '설비 상태 및 비가동 이력 관리',
      '품질 검사 결과 연계',
      '전자 배치기록 관리',
      '공정별 KPI 및 대시보드',
      '이상 발생 알림 및 조치 이력 관리',
    ],
    effects: [
      '생산 진행 가시성 향상',
      '공정 리드타임 관리 강화',
      '보고 체계 간소화',
      '품질 데이터 신뢰도 향상',
      '제조 운영 의사결정 속도 향상',
    ],
  },
  {
    id: 'wms',
    href: '/solution/wms.html',
    navLabel: 'withWMS',
    badge: 'Warehouse Management System',
    title: 'withWMS 창고관리 시스템',
    summary: '원료, 부자재, 반제품, 완제품의 입출고, 보관 위치, 재고, 이동 이력을 체계적으로 관리하는 창고 운영 시스템입니다. 바코드 기반 업무 흐름으로 재고 오차를 줄이고 Lot, 사용기한, 보관 위치 중심의 물류 추적성을 높입니다.',
    portfolioTitle: ['입고부터 출고까지', '자재 흐름을 정확하게 추적합니다'],
    highlights: [
      {
        label: '핵심 포인트',
        text: '입고, 보관, 이동, 출고 데이터를 연결해 창고 운영 정확도를 높입니다.',
      },
      {
        label: '운영 방식',
        text: '바코드 기반 작업으로 자재 위치와 이동 이력을 관리합니다.',
      },
      {
        label: '데이터 관리',
        text: 'Lot, 사용기한, 위치, 재고 수량을 한 화면에서 조회합니다.',
      },
    ],
    portfolioDesc: '입고 시 원료와 Lot 정보를 등록하고 보관 위치를 지정한 뒤, 생산 요청, 출고 지시, 이동, 실사를 바코드 스캔 기반으로 처리합니다.',
    keyModules: [
      {
        label: '입고 · 검수 관리',
        icon: 'scan',
        desc: '입고 자재의 품목, Lot, 수량, 검수 상태를 등록하고 보관 전 확인 절차를 표준화합니다.',
      },
      {
        label: 'Lot · 사용기한 · 위치 추적',
        icon: 'location',
        desc: '자재 Lot, 사용기한, 보관 위치를 연결해 창고 내 이동과 재고 상태를 추적합니다.',
      },
      {
        label: '생산 출고 · 반납 관리',
        icon: 'clock',
        desc: '생산 요청에 따른 출고와 미사용 자재 반납을 작업 흐름으로 관리해 재고 오차를 줄입니다.',
      },
      {
        label: '재고 실사 · 라벨 발행',
        icon: 'chart',
        desc: '재고 실사 결과와 라벨 발행 이력을 관리해 창고 운영의 정확성과 추적성을 높입니다.',
      },
    ],
    features: [
      '입고 등록 및 검수 관리',
      '보관 위치 관리',
      'Lot 및 사용기한 관리',
      '재고 이동 이력 관리',
      '생산 출고 및 반납 관리',
      '출하 관리',
      '재고 실사',
      '바코드 라벨 발행',
    ],
    effects: [
      '재고 정확도 향상',
      '입출고 처리 시간 단축',
      '자재 추적성 강화',
      '창고 운영 표준화',
      '생산 지연 요인 감소',
    ],
  },
];
